import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, XCircle, LogOut, ChevronDown, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';

const APRES_TESTS = ['tests_passes', 'accepte', 'refuse', 'abandon'];
const DECISIONS = [
  { id: 'accepte', label: 'Accepté', icon: CheckCircle, cls: 'text-green-600' },
  { id: 'refuse', label: 'Refusé', icon: XCircle, cls: 'text-red-600' },
  { id: 'abandon', label: 'Abandon', icon: LogOut, cls: 'text-slate-600' },
];

const AbandonDialog = ({ open, onOpenChange, motifInitial, onValider, saving }) => {
  const [motif, setMotif] = useState(motifInitial || '');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="abandon-dialog" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>Abandon du candidat</DialogTitle>
          <DialogDescription>Note interne : le candidat n'est pas prévenu.</DialogDescription>
        </DialogHeader>
        <Label htmlFor="motif-abandon">Motif de l'abandon *</Label>
        <Textarea id="motif-abandon" rows={3} value={motif} onChange={(e) => setMotif(e.target.value)}
          placeholder="Ex. : pas de financement, changement d'orientation…" data-testid="input-motif-abandon" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="btn-abandon-annuler">Annuler</Button>
          <Button onClick={() => onValider(motif)} disabled={!motif.trim() || saving} className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="btn-abandon-valider">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Enregistrer l'abandon
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Décision après les tests (Accepté / Refusé / Abandon), modifiable à tout moment
export const DecisionCandidat = ({ candidatId, statut, motif, onChanged, header = false }) => {
  const [saving, setSaving] = useState(false);
  const [abandonOpen, setAbandonOpen] = useState(false);
  if (!APRES_TESTS.includes(statut)) return null;
  const premiere = statut === 'tests_passes';

  const enregistrer = async (decision, motifAbandon = '') => {
    setSaving(true);
    try {
      const { data } = await axios.post(`/candidats/${candidatId}/decision`, { decision, motif: motifAbandon });
      toast.success(data.message);
      setAbandonOpen(false);
      onChanged?.(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const choisir = (d) => {
    if (d === 'abandon') { setAbandonOpen(true); return; }
    const label = DECISIONS.find(x => x.id === d).label;
    const msg = premiere ? `Marquer ce candidat « ${label} » ? Il sera prévenu dans son espace.` : `Passer la décision à « ${label} » ? (le candidat n'est pas prévenu)`;
    if (window.confirm(msg)) enregistrer(d);
  };

  return (
    <div onClick={(e) => e.stopPropagation()} className="contents">
      {premiere ? (
        DECISIONS.map(({ id, label, icon: Icon }) => (
          <Button key={id} size="sm" disabled={saving} onClick={() => choisir(id)} data-testid={`btn-decision-${id}-${candidatId}`}
            className={id === 'accepte' ? 'bg-green-600 hover:bg-green-700 text-white' : id === 'refuse' ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-slate-600 hover:bg-slate-700 text-white'}>
            <Icon className="h-4 w-4 mr-1" /> {id === 'accepte' ? 'Accepter' : id === 'refuse' ? 'Refuser' : 'Abandon'}
          </Button>
        ))
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="outline" disabled={saving} data-testid={`btn-modifier-decision-${candidatId}`}
              className={header ? 'bg-white/20 hover:bg-white/30 text-white border-white/30' : ''}>
              Modifier la décision <ChevronDown className="h-4 w-4 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {DECISIONS.map(({ id, label, icon: Icon, cls }) => (
              <DropdownMenuItem key={id} disabled={id === statut && id !== 'abandon'} onClick={() => choisir(id)} data-testid={`decision-option-${id}-${candidatId}`}>
                <Icon className={`h-4 w-4 mr-2 ${cls}`} /> {label}{id === statut ? ' (actuel)' : ''}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {abandonOpen && (
        <AbandonDialog open={abandonOpen} onOpenChange={setAbandonOpen} motifInitial={motif} saving={saving}
          onValider={(m) => enregistrer('abandon', m)} />
      )}
    </div>
  );
};
